import {useMemo, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {Alert, Button, Form, Input, Select, Spin, Tabs, Tag, Empty} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import DBModal from 'components/modal/modal';
import {fieldRules} from 'utils/helper';
import {
  GET_SKILL_MARKDOWN_TRANSLATIONS,
  CREATE_SKILL_TRANSLATION,
  UPDATE_SKILL_TRANSLATION,
} from './resolvers';
import {SKILL_LANGUAGE_OPTIONS, SKILL_STATUS_TAG_COLOR} from './skillConstants';
import {formatScenarioTreeForDisplay} from './skillScenarioFormat';
import {formatUserDisplayName} from './userDisplay';
import styles from './aiAutomation.module.scss';

const formatDate = value =>
  value ? moment(value).format('DD MMM YYYY, hh:mm A') : '-';

const CLOSED_MODAL = {open: false, mode: 'create', record: null};

/**
 * Lightweight markdown-to-HTML converter for rendering skill content.
 * Handles headings, bold, italic, code blocks, inline code, links, lists,
 * blockquotes, horizontal rules, and paragraphs.
 */
const renderMarkdownToHtml = md => {
  if (!md) return '';
  let html = md
    // Code blocks (fenced)
    .replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
      const escaped = code
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return `<pre><code class="${lang}">${escaped}</code></pre>`;
    })
    // Headings
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Horizontal rule
    .replace(/^---$/gm, '<hr/>')
    // Blockquotes
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
    // Bold + italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Links
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
    )
    // Unordered lists
    .replace(/^[*-] (.+)$/gm, '<li>$1</li>')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    // Line breaks → paragraphs for remaining plain lines
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/\n/g, '<br/>');

  // Wrap consecutive <li> elements in <ul>
  html = html.replace(/((?:<li>.*?<\/li>\s*(?:<br\/>)?)+)/g, match => {
    const cleaned = match.replace(/<br\/>/g, '');
    return `<ul>${cleaned}</ul>`;
  });

  return `<p>${html}</p>`
    .replace(/<p><\/p>/g, '')
    .replace(/<p>(<h[1-4]>)/g, '$1')
    .replace(/(<\/h[1-4]>)<\/p>/g, '$1')
    .replace(/<p>(<pre>)/g, '$1')
    .replace(/(<\/pre>)<\/p>/g, '$1')
    .replace(/<p>(<hr\/>)<\/p>/g, '$1')
    .replace(/<p>(<ul>)/g, '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1')
    .replace(/<p>(<blockquote>)/g, '$1')
    .replace(/(<\/blockquote>)<\/p>/g, '$1');
};

const TranslationForm = ({form, mode, onFinish, submitting, language}) => {
  const isEdit = mode === 'edit';
  const markdownValue = Form.useWatch('contentMarkdown', form);

  return (
    <div className={styles.formRoot}>
      <h2>
        {isEdit ? 'Update Translation' : 'Create Translation'}{' '}
        <Tag color="blue">{language}</Tag>
      </h2>
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item name="title" label="Title" rules={[fieldRules('Title')]}>
          <Input placeholder="Enter translation title" />
        </Form.Item>
        <Form.Item
          name="description"
          label='Description ("Use when…" hint shown to the AI registry)'
          tooltip="Phrase as a triggering condition, e.g. “Use when the user is asking about deposits — pending, failed, or successful.” Required for the AI to route to this skill."
          rules={[fieldRules('Description')]}>
          <Input.TextArea
            placeholder="Use when the user is asking about…"
            rows={3}
          />
        </Form.Item>
        <Form.Item
          name="contentMarkdown"
          rules={[fieldRules('Content')]}
          hidden>
          <Input />
        </Form.Item>
        <div style={{marginBottom: 16}}>
          <label className={styles.contentLabel}>
            <span style={{color: '#ff4d4f', marginRight: 4}}>*</span>
            Content (Markdown)
          </label>
          <Tabs
            defaultActiveKey="write"
            destroyInactiveTabPane={false}
            items={[
              {
                key: 'write',
                label: 'Write',
                children: (
                  <Input.TextArea
                    value={markdownValue || ''}
                    onChange={e =>
                      form.setFieldValue('contentMarkdown', e.target.value)
                    }
                    placeholder="Enter markdown content"
                    rows={14}
                    style={{fontFamily: 'monospace', fontSize: 13}}
                  />
                ),
              },
              {
                key: 'preview',
                label: 'Preview',
                children: markdownValue ? (
                  <div
                    className={styles.previewPane}
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdownToHtml(markdownValue),
                    }}
                  />
                ) : (
                  <div className={styles.previewEmpty}>Nothing to preview</div>
                ),
              },
            ]}
          />
        </div>
        <div className={styles.formActions}>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {isEdit ? 'Save Changes' : 'Create Translation'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export const SkillTranslations = () => {
  const {skillId} = useParams();
  const navigate = useNavigate();
  const {state: routeState} = useLocation();
  const loggedInUserId = useSelector(state => state?.auth?.userData?.user?.id);

  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [modal, setModal] = useState(CLOSED_MODAL);
  const [form] = Form.useForm();

  const {data, loading, refetch, error} = useQuery(
    GET_SKILL_MARKDOWN_TRANSLATIONS,
    {
      variables: {filter: {skillId}},
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
      skip: !skillId,
    },
  );

  const response = data?.getSkillTranslations;
  const isError = response?.__typename === 'Error';
  const translations =
    response?.__typename === 'ResponseWithSkillTranslations'
      ? response.data
      : [];
  const errorMessage = isError ? response?.message : error?.message;
  const skill = routeState?.skill || {};
  const skillTitle = [skill.node, formatScenarioTreeForDisplay(skill.tree)]
    .filter(Boolean)
    .join(' / ');

  // Set of languages that already have translations
  const existingLanguages = useMemo(
    () => translations.map(t => t.language),
    [translations],
  );

  // Build language selector: all supported languages, mark existing ones
  const languageOptions = useMemo(
    () =>
      SKILL_LANGUAGE_OPTIONS.map(opt => ({
        ...opt,
        label: existingLanguages.includes(opt.value)
          ? `${opt.label}`
          : `${opt.label} (new)`,
      })),
    [existingLanguages],
  );

  // Auto-select first language if none selected
  const activeLanguage =
    selectedLanguage ||
    (translations.length > 0 ? translations[0].language : null);

  const activeTranslation = translations.find(
    t => t.language === activeLanguage,
  );

  const languageExists = existingLanguages.includes(activeLanguage);

  // Mutations
  const [createTranslation, {loading: creating}] = useMutation(
    CREATE_SKILL_TRANSLATION,
    {
      errorPolicy: 'all',
      onError: err => toast.error(err.message),
    },
  );

  const [updateTranslation, {loading: updating}] = useMutation(
    UPDATE_SKILL_TRANSLATION,
    {
      errorPolicy: 'all',
      onError: err => toast.error(err.message),
    },
  );

  const submitting = creating || updating;

  const handleGoBack = () => {
    navigate('/dashboard/ai-automation/skills');
  };

  const openCreate = () => {
    form.resetFields();
    setModal({open: true, mode: 'create', record: null});
  };

  const openEdit = () => {
    if (!activeTranslation) return;
    form.setFieldsValue({
      title: activeTranslation.title,
      description: activeTranslation.description,
      contentMarkdown: activeTranslation.contentMarkdown,
    });
    setModal({open: true, mode: 'edit', record: activeTranslation});
  };

  const closeModal = () => {
    setModal(CLOSED_MODAL);
    form.resetFields();
  };

  const handleSubmit = async values => {
    if (modal.mode === 'edit') {
      const input = {
        id: modal.record?.id,
        language: activeLanguage,
        title: values.title,
        description: values.description,
        contentMarkdown: values.contentMarkdown,
        ...(loggedInUserId ? {updatedBy: String(loggedInUserId)} : {}),
      };
      const {data: result} = await updateTranslation({variables: {input}});
      const payload = result?.updateSkillTranslation;
      if (!payload) return;
      if (payload.__typename === 'Error') {
        toast.error(payload.message || 'Failed to update translation');
        return;
      }
      toast.success(payload.message || 'Translation updated successfully');
      closeModal();
      refetch();
      return;
    }

    // Create mode
    const input = {
      skillId,
      ...(loggedInUserId ? {createdBy: String(loggedInUserId)} : {}),
      translations: [
        {
          title: values.title,
          language: activeLanguage,
          description: values.description,
          contentMarkdown: values.contentMarkdown,
        },
      ],
    };
    const {data: result} = await createTranslation({variables: {input}});
    const payload = result?.createSkillTranslations;
    if (!payload) return;
    if (payload.__typename === 'Error') {
      toast.error(payload.message || 'Failed to create translation');
      return;
    }
    toast.success(payload.message || 'Translation created successfully');
    closeModal();
    refetch();
  };

  return (
    <div className={styles.skillsRoot}>
      <div className={styles.skillsToolbar}>
        <div className={styles.filters}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleGoBack}
            type="text">
            Back to Skills
          </Button>
          <Select
            className={styles.filterSelect}
            placeholder="Select language"
            value={activeLanguage}
            onChange={value => setSelectedLanguage(value)}
            options={languageOptions}
          />
        </div>
        <div className={styles.filters}>
          {activeLanguage && languageExists && (
            <Button icon={<EditOutlined />} onClick={openEdit}>
              Edit
            </Button>
          )}
          {activeLanguage && !languageExists && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Create Translation
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      <div className={styles.translationContextCard}>
        <div>
          <p className={styles.contextEyebrow}>Skill translations</p>
          <h2>{skillTitle || 'Selected skill'}</h2>
        </div>
        <div className={styles.contextDetails}>
          <span>
            <strong>Category</strong>
            {skill.node || '-'}
          </span>
          <span>
            <strong>Scenario</strong>
            {formatScenarioTreeForDisplay(skill.tree) || '-'}
          </span>
          <span>
            <strong>Team</strong>
            {skill.domain || '-'}
          </span>
          <span>
            <strong>Status</strong>
            {skill.status ? (
              <Tag color={SKILL_STATUS_TAG_COLOR[skill.status] || 'default'}>
                {skill.status}
              </Tag>
            ) : (
              '-'
            )}
          </span>
        </div>
      </div>

      {errorMessage && (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          className={styles.errorAlert}
        />
      )}

      {skill.status === 'PUBLISHED' &&
        translations.some(t => !(t.description || '').trim()) && (
          <Alert
            type="warning"
            showIcon
            message="Published skill missing description"
            description="One or more translations have no “Use when…” description. The AI registry can't route to this skill reliably without it."
            className={styles.errorAlert}
          />
        )}

      {loading && (
        <div className={styles.translationLoading}>
          <Spin size="large" />
        </div>
      )}

      {!loading && !errorMessage && !activeLanguage && (
        <Empty description="Select a language to view or create a translation" />
      )}

      {!loading && !errorMessage && activeLanguage && !languageExists && (
        <Empty
          description={`No translation exists for "${
            SKILL_LANGUAGE_OPTIONS.find(o => o.value === activeLanguage)
              ?.label || activeLanguage
          }" yet`}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Create Translation
          </Button>
        </Empty>
      )}

      {!loading && activeTranslation && (
        <div className={styles.translationCard}>
          <div className={styles.translationHeader}>
            <div>
              <p className={styles.contextEyebrow}>Active translation</p>
              <h2 className={styles.translationTitle}>
                {activeTranslation.title || 'Untitled'}
              </h2>
            </div>
            <Tag color="blue">{activeTranslation.language}</Tag>
          </div>

          <div className={styles.translationMeta}>
            <span>
              Created by: {formatUserDisplayName(activeTranslation.createdBy)}
            </span>
            <span>
              Updated by: {formatUserDisplayName(activeTranslation.updatedBy)}
            </span>
            <span>Created: {formatDate(activeTranslation.createdAt)}</span>
            <span>Updated: {formatDate(activeTranslation.updatedAt)}</span>
          </div>

          {activeTranslation.description ? (
            <div className={styles.translationDescription}>
              {activeTranslation.description}
            </div>
          ) : null}

          <div
            className={styles.markdownContent}
            dangerouslySetInnerHTML={{
              __html: renderMarkdownToHtml(activeTranslation.contentMarkdown),
            }}
          />
        </div>
      )}

      <DBModal
        isOpen={modal.open}
        handleClose={closeModal}
        destroyOnClose
        content={
          <TranslationForm
            form={form}
            mode={modal.mode}
            onFinish={handleSubmit}
            submitting={submitting}
            language={
              SKILL_LANGUAGE_OPTIONS.find(o => o.value === activeLanguage)
                ?.label || activeLanguage
            }
          />
        }
      />
    </div>
  );
};
